import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Confetti } from '../ui/Confetti';
import { playSound } from '../../utils/sound-effects';
import { useShowRewards } from '../../hooks/useShowRewards';
import { useCelebrationStore } from '../../store/celebrationStore';
import { getBadgeById } from '../../engine/badge-evaluator';

/**
 * App-wide badge-earned celebration. Mounted once in AppShell; reveals each
 * badge from the celebration queue one at a time (zoom + confetti + sound,
 * with the badge name read aloud). Previously badges only flashed a
 * notification-bell dot while level-ups and streaks got full celebrations —
 * yet badges are the most collectible, most age-apt reward for 3-10 year olds.
 */
export function BadgeEarnedModal() {
  const showRewards = useShowRewards();
  const currentBadgeId = useCelebrationStore((s) => s.badgeQueue[0] ?? null);
  const dismiss = useCelebrationStore((s) => s.dismissCurrentBadge);
  const [showConfetti, setShowConfetti] = useState(false);

  const badge = currentBadgeId ? getBadgeById(currentBadgeId) : undefined;

  // Auto-advance through the queue after a beat per badge.
  useEffect(() => {
    if (!badge) return;
    const timer = setTimeout(dismiss, 3800);
    return () => clearTimeout(timer);
  }, [badge, currentBadgeId, dismiss]);

  // Confetti + sound + spoken name on each reveal.
  useEffect(() => {
    if (badge && showRewards) {
      setShowConfetti(true);
      playSound('levelup');
      try {
        const utter = new SpeechSynthesisUtterance(`You earned the ${badge.name} badge!`);
        utter.rate = 0.95;
        window.speechSynthesis?.speak(utter);
      } catch {
        // narration is a nicety — never block the celebration
      }
    } else {
      setShowConfetti(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBadgeId]);

  // If rewards are off, silently drain the queue so it can't wedge.
  useEffect(() => {
    if (badge && !showRewards) dismiss();
  }, [badge, showRewards, dismiss]);

  if (!badge || !showRewards) return null;

  return (
    <AnimatePresence>
      <Confetti active={showConfetti} duration={3000} particleCount={45} />
      <motion.div
        key={currentBadgeId}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={dismiss}
      >
        <motion.div
          initial={{ scale: 0, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          exit={{ scale: 0 }}
          transition={{ type: 'spring', damping: 12, stiffness: 200 }}
          onClick={(e) => e.stopPropagation()}
          className="rounded-3xl p-8 w-full max-w-sm shadow-2xl text-center text-white"
          style={{ background: 'linear-gradient(to bottom right, #f59e0b, #d97706)' }}
        >
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: [0, 1.3, 0.9, 1.1, 1] }}
            transition={{ delay: 0.15, duration: 0.8, times: [0, 0.3, 0.5, 0.7, 1], type: 'spring', bounce: 0.6 }}
            className="text-7xl mb-4"
          >
            {badge.emoji}
          </motion.div>

          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
            <p className="text-lg font-medium opacity-80">Badge Earned!</p>
            <h2 className="text-2xl font-bold mb-2">{badge.name}</h2>
            <p className="text-base opacity-90">{badge.description}</p>
          </motion.div>

          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            whileTap={{ scale: 0.95 }}
            onClick={dismiss}
            className="mt-6 bg-white px-8 py-3 rounded-xl font-bold text-lg text-amber-700 hover:bg-amber-50 transition-colors"
          >
            Awesome!
          </motion.button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
